import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Row, Col, Card, Typography, Avatar, Tag, Space, Button, Spin, message } from 'antd';
import { UserOutlined, EditOutlined, ArrowLeftOutlined, PhoneOutlined, MailOutlined, CalendarOutlined } from '@ant-design/icons';
import CoachStatsCard from '../components/shared/CoachStatsCard';
import { apiService } from '../../../services/api';
import { Coach, CoachPerformance } from '../types';
import { transformCoachToLegacy, formatCoachName, getCoachEmail, getCoachPhone, getCoachStatus, getCoachClientCount } from '../utils/coachDataTransform';

const { Title, Text } = Typography;

const CoachDetails: React.FC = () => {
  const { coachId } = useParams<{ coachId: string }>();
  const [coach, setCoach] = useState<Coach | null>(null);
  const [performance, setPerformance] = useState<CoachPerformance | null>(null);
  const [loading, setLoading] = useState(false);

  const loadCoachData = async (id: string) => {
    setLoading(true);
    try {
      // Load coach details
      const coachResponse = await apiService.getCoachById(id);
      
      if (coachResponse.success && coachResponse.data) {
        const coachData = coachResponse.data.data;
        setCoach(coachData);

        // Load coach performance data
        try {
          const performanceResponse = await apiService.getCoachPerformance(id);
          if (performanceResponse.success && performanceResponse.data) {
            setPerformance(performanceResponse.data.data);
          }
        } catch (perfError) {
          console.warn('Could not load performance data:', perfError);
          // Continue without performance data
        }
      } else {
        message.error('Coach not found');
      }
    } catch (error) {
      console.error('Error loading coach data:', error);
      message.error('Error loading coach details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (coachId) {
      loadCoachData(coachId);
    }
  }, [coachId]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!coach) {
    return (
      <Card>
        <Text>Coach not found</Text>
      </Card>
    );
  }

  const status = getCoachStatus(coach);
  const clientCount = getCoachClientCount(coach);
  const coachName = formatCoachName(coach);
  const email = getCoachEmail(coach);
  const phone = getCoachPhone(coach);

  return (
    <div>
      <Card style={{ marginBottom: 24 }}>
        <Space style={{ marginBottom: 16 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => window.history.back()}>
            Back
          </Button>
          <Button type="primary" icon={<EditOutlined />}>
            Edit Coach
          </Button>
        </Space>

        <Space align="start" size="large">
          <Avatar 
            size={80} 
            src={coach.user.profilePicture} 
            icon={<UserOutlined />} 
          />
          <div>
            <Title level={2} style={{ margin: 0 }}>
              {coachName}
            </Title>
            <Text type="secondary" style={{ fontSize: '16px' }}>
              <MailOutlined style={{ marginRight: 8 }} />
              {email}
            </Text>
            <br />
            <Text type="secondary" style={{ fontSize: '14px' }}>
              <PhoneOutlined style={{ marginRight: 8 }} />
              {phone}
            </Text>
            <br />
            <Space size="small" style={{ marginTop: 8 }}>
              <Tag color={status === 'Active' ? 'green' : status === 'Onboarding' ? 'blue' : 'red'}>
                {status}
              </Tag>
              {coach.specializations.slice(0, 3).map(spec => (
                <Tag key={spec}>{spec}</Tag>
              ))}
              {coach.specializations.length > 3 && (
                <Tag>+{coach.specializations.length - 3} more</Tag>
              )}
            </Space>
          </div>
        </Space>
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <CoachStatsCard
            title="Performance Metrics"
            stats={[
              { 
                title: 'Client Count', 
                value: clientCount 
              },
              { 
                title: 'Client Retention', 
                value: performance?.clientRetentionRate || 0, 
                precision: 1, 
                suffix: '%' 
              },
              { 
                title: 'Client Rating', 
                value: performance?.averageClientRating || 0, 
                precision: 1,
                suffix: '/5'
              },
            ]}
          />
        </Col>
        
        <Col xs={24} md={12}>
          <CoachStatsCard
            title="Coach Information"
            stats={[
              { 
                title: 'Experience', 
                value: coach.experience || 0, 
                suffix: ' years' 
              },
              { 
                title: 'Max Clients', 
                value: coach.maxClients 
              },
              { 
                title: 'Hourly Rate', 
                value: coach.hourlyRate || 0, 
                prefix: '$' 
              },
            ]}
          />
        </Col>

        {performance && (
          <Col xs={24} md={12}>
            <CoachStatsCard
              title="Detailed Performance"
              stats={[
                { 
                  title: 'Programs Created', 
                  value: performance.programsCreated 
                },
                { 
                  title: 'Onboarding Rate', 
                  value: performance.onboardingCompletionRate, 
                  suffix: '%' 
                },
                { 
                  title: 'Revenue Generated', 
                  value: performance.revenueGenerated, 
                  prefix: '$' 
                },
              ]}
            />
          </Col>
        )}

        <Col xs={24} md={performance ? 12 : 24}>
          <Card title="Additional Information">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text strong>Email:</Text> {email}
              </div>
              <div>
                <Text strong>Phone:</Text> {phone}
              </div>
              <div>
                <Text strong>Assigned Clients:</Text> {clientCount}
              </div>
              <div>
                <Text strong>
                  <CalendarOutlined style={{ marginRight: 8 }} />
                  Joined Date:
                </Text> {new Date(coach.createdAt).toLocaleDateString()}
              </div>
              <div>
                <Text strong>Last Updated:</Text> {new Date(coach.updatedAt).toLocaleDateString()}
              </div>
              {coach.bio && (
                <div>
                  <Text strong>Bio:</Text>
                  <div style={{ marginTop: 8, padding: '12px', backgroundColor: '#fafafa', borderRadius: '6px' }}>
                    {coach.bio}
                  </div>
                </div>
              )}
              {coach.certifications.length > 0 && (
                <div>
                  <Text strong>Certifications:</Text>
                  <div style={{ marginTop: 8 }}>
                    {coach.certifications.map(cert => (
                      <Tag key={cert} color="blue" style={{ marginBottom: 4 }}>{cert}</Tag>
                    ))}
                  </div>
                </div>
              )}
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default CoachDetails; 